import type { FC } from "react";
import { useCallback } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";

import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { EHooksVersions } from "app/services/SwHook";

import calendarImg from "./svg/calendar.svg";

interface ICouldBeLarge {
    large?: boolean;
}

export const MoreDataBannerContainer = styled.span<ICouldBeLarge>`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px 16px 16px;
    background: rgba(79, 141, 249, 0.1);
    border: 0;
    margin: 0;
    cursor: ${(p) => (p.large ? "default" : "pointer")};

    &.largeBannerDatePicker {
        width: calc(100% - 32px);
    }

    button {
        font-size: 14px;
        font-weight: 700;
        float: left;
        margin-left: 31px;
        margin-top: 6px;
        font-family: "DM Sans" !important;

        > .SWReactIcons {
            width: 18px;
            margin-right: 5px;
        }
    }
`;

const PresentationSection = styled.span<ICouldBeLarge>`
    display: flex;
    max-width: ${(p) => (p.large ? "100%" : "270px")};
`;

const Title = styled.span<ICouldBeLarge>`
    display: block;
    margin: ${(p) => (p.large ? "0px" : "2px")} 0 6px;
    font-size: 14px;
    line-height: 16px;
`;

export const TextSection = styled.span`
    display: block;
    color: ${colorsPalettes.indigo[300]};
    text-align: left;
    margin-left: 8px;
`;

const Subtitle = styled.span`
    display: block;
    font-size: 12px;
    line-height: 16px;
`;

const ImgSection = styled.span`
    display: block;
    flex-shrink: 0;
`;

const MoreDataBannerContainerABtest = styled(MoreDataBannerContainer)`
    ${PresentationSection} {
        max-width: ${(p) => (p.large ? "100%" : "275px")};
    }

    &.smallBannerDatePicker {
        display: block;
        width: 100%;
    }

    &.largeBannerDatePicker {
        align-items: center;
        padding: 22px;
        width: calc(100% - 44px);

        button {
            margin-top: 0;
        }

        ${Title} {
            margin-bottom: 0;
            font-size: 16px;
        }

        ${PresentationSection} {
            align-items: center;
            display: flex;
        }

        ${TextSection} {
            margin-left: 16px;
            font-weight: 500;
        }
    }
`;

interface IMoreDataBanner extends ICouldBeLarge {
    onClick: VoidFunction;
}

export const MoreDataBanner: FC<IMoreDataBanner> = ({ onClick, large = false }) => {
    const translate = useTranslation();

    const handleDatepickerBtnClick = useCallback(async () => {
        await handleCTAClick(EHooksVersions.V4, "allowedDurationDate", TestCases.TRIAL_AFTER_TRIAL);
        onClick();
    }, [onClick]);

    const moreDataBanner = () => {
        return (
            <MoreDataBannerContainerABtest
                large={large}
                as={large ? "div" : "button"}
                onClick={handleDatepickerBtnClick}
                className={!large ? "smallBannerDatePicker" : "largeBannerDatePicker"}
            >
                <PresentationSection large={large}>
                    <ImgSection>
                        <img
                            src={calendarImg}
                            alt=""
                        />
                    </ImgSection>
                    <TextSection>
                        <Title large={large}>{translate("duration_selector.more_data.trial.title")}</Title>
                    </TextSection>
                </PresentationSection>
                <div>
                    <Button type="trial">
                        <SWReactIcons
                            size={"sm"}
                            iconName={"sparkles"}
                        />
                        {translate("duration_selector.more_data.trial.learn_more")}
                    </Button>
                </div>
            </MoreDataBannerContainerABtest>
        );
    };
    return moreDataBanner();
};
